import { useState, useEffect } from 'react'
import { Modal } from '@/components/common'
import { directorApi } from '@/services/api/director'
import type { DirectorConfig, DirectorDecisionCreatePayload, DirectorActionItem, DirectorRequestDetail } from '@/types/director'
import { extractErrorMessage, isDirectorDecisionConflict } from '@/utils/errorHandler'
import { CheckCircle2 } from 'lucide-react'
import { lessonRange } from '@/components/effective-plan/planFormat'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

function formatTableDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return iso
  }
}

interface QuickApproveModalProps {
  isOpen: boolean
  /** Fallakte v2 (P5.10): Minimal-Interface — Dashboard-Items erfüllen es ebenfalls */
  item: DirectorActionItem | null
  config: DirectorConfig | null
  onClose: () => void
  onSuccess: () => void
  /** Fallakte v2 (K7): 409-Konflikt → Liste refreschen */
  onConflict?: () => void
  /** Fallakte v3 (B3): Lektionen können mitgegeben werden — dann kein eigener Detail-Fetch. */
  lessons?: DirectorRequestDetail['lessons'] | null
  initialPaymentType?: 'private' | 'school'
}

export function QuickApproveModal({ isOpen, item, config, onClose, onSuccess, onConflict, lessons, initialPaymentType }: QuickApproveModalProps) {
  const [paymentType, setPaymentType] = useState<'private' | 'school'>('private')
  const [docRequests, setDocRequests] = useState<string[]>([])
  const [customDoc, setCustomDoc] = useState('')
  const [showDocs, setShowDocs] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lessonRows, setLessonRows] = useState<DirectorRequestDetail['lessons'] | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    if (isOpen && item) {
      setPaymentType(initialPaymentType ?? 'private')
      setDocRequests([])
      setCustomDoc('')
      setShowDocs(false)
      setError(null)
      if (lessons) {
        // v3 (B3): Fallakte reicht die Lektionen durch — kein zweiter Fetch
        setLessonRows(lessons)
        setDetailLoading(false)
        return
      }
      setLessonRows(null)
      if (item.kind === 'retro') {
        setDetailLoading(false)
        return
      }
      setDetailLoading(true)
      directorApi.getRequestDetail(item.id)
        .then(d => setLessonRows(d.lessons))
        .catch(() => setLessonRows(null))
        .finally(() => setDetailLoading(false))
    }
  }, [isOpen, item, lessons, initialPaymentType])

  if (!item) return null

  const handlePaymentChange = (type: 'private' | 'school') => {
    setPaymentType(type)
  }

  const toggleDoc = (name: string) => {
    setDocRequests(prev => prev.includes(name) ? prev.filter(d => d !== name) : [...prev, name])
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      if (item.kind === 'retro') {
        await directorApi.setRetroPayer(item.id, paymentType)
      } else {
        const payload: DirectorDecisionCreatePayload = {
          substitution_request_id: item.id,
          payment_type: paymentType,
          decision_type: 'approved',
          document_requests: docRequests.length > 0 ? docRequests : undefined,
          custom_document_request: customDoc || undefined,
        }
        await directorApi.createDecision(payload)
      }
      onSuccess()
    } catch (e: unknown) {
      const msg = extractErrorMessage(e, { loadError: 'Ein Fehler ist aufgetreten' })
      if (isDirectorDecisionConflict(e)) {
        setError('Diese Anfrage wurde bereits von einem anderen Direktor entschieden.')
        onConflict?.()
      } else {
        setError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Vertretung genehmigen" size="lg">
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{item.requesting_teacher_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.lesson_count} Lektionen
          </p>
          {item.reason && <p className="text-xs text-gray-500 italic mt-1">{item.reason}</p>}
        </div>

        {detailLoading && <p className="text-xs text-gray-500">Lektionen werden geladen...</p>}
        {lessonRows && lessonRows.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="w-full text-xs bg-white" aria-label="Lektionen der Anfrage">
              <thead className="text-[10px] text-gray-500 uppercase bg-gray-100 border-b">
                <tr>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Datum</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Zeit</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Fach</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Klasse</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Raum</th>
                  <th scope="col" className="py-1.5 px-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortLessonsByDateTime(lessonRows).map(l => {
                    const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']
                    const isAssigned = Boolean(
                      l.assigned_teacher_id && COMMITTED.includes(l.status ?? ''),
                    )
                    const time = lessonRange(l, '-')
                    return (
                      <tr key={l.id}>
                        <td className="py-1.5 px-2">{formatTableDate(l.date)}</td>
                        <td className="py-1.5 px-2 font-semibold">{time || '—'}</td>
                        <td className="py-1.5 px-2 font-medium">{l.subject_name || '—'}</td>
                        <td className="py-1.5 px-2">{l.klass_name || '—'}</td>
                        <td className="py-1.5 px-2">{l.room_name || '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          {isAssigned
                            ? `✓ ${l.assigned_teacher_name || 'Vergeben'}`
                            : l.status === 'cancelled'
                              ? '✗ Storniert'
                              : '○ Offen'}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Zahlungsart</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="payment"
                checked={paymentType === 'private'}
                onChange={() => handlePaymentChange('private')}
                className="w-4 h-4"
              />
              <span className="text-sm">Privat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="payment"
                checked={paymentType === 'school'}
                onChange={() => handlePaymentChange('school')}
                className="w-4 h-4"
              />
              <span className="text-sm">Schule</span>
            </label>
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowDocs(!showDocs)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <span>{showDocs ? '▾' : '▸'}</span>
            Dokumente anfordern (optional)
          </button>
          {showDocs && (
            <div className="mt-2 space-y-2 pl-2">
              {(Array.isArray(config?.director_document_types)
                ? (config!.director_document_types as unknown[])
                    .map(d => typeof d === 'string' ? d : ((d as { name?: string })?.name ?? ''))
                    .filter(Boolean)
                : []).map(d => (
                <label key={d} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={docRequests.includes(d)}
                    onChange={() => toggleDoc(d)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{d}</span>
                </label>
              ))}
              <div>
                <input
                  type="text"
                  value={customDoc}
                  onChange={e => setCustomDoc(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-1.5 text-sm"
                  placeholder="Eigenes Dokument..."
                />
              </div>
            </div>
          )}
        </div>

        {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
          >
            <CheckCircle2 className="w-4 h-4" />
            {submitting ? 'Speichern...' : 'Genehmigen'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </Modal>
  )
}
